/**
 * Catalogos para los selectores y las etiquetas de la UI.
 *
 * Los VALORES se declaran aca (con `satisfies` contra los tipos de
 * `@amoble/contracts`, asi el compilador falla si el contrato cambia) porque la
 * UI necesita ademas un texto en espanol que el contrato no tiene por que
 * conocer. No se duplican tipos: se duplica solo la traduccion.
 */
import type {
  FlowId,
  FlowRunStatus,
  MessageDirection,
  MessageStatus,
  System,
} from '../api/contracts';

export interface Option<T extends string> {
  readonly value: T;
  readonly label: string;
}

export const SYSTEM_OPTIONS = [
  { value: 'janis', label: 'Janis OMS' },
  { value: 'sap', label: 'SAP B1' },
  { value: 'altanet', label: 'Altanet WMS' },
  { value: 'multivende', label: 'Multivende' },
  { value: 'hubspot', label: 'HubSpot' },
  { value: 'syncro', label: 'Syncro' },
  { value: 'decorapos', label: 'DecoraPOS' },
  { value: 'invoicer', label: 'Facturador' },
  { value: 'n8n', label: 'n8n' },
  { value: 'bcentral', label: 'Banco Central' },
  { value: 'pos', label: 'POS legado' },
  { value: 'goodworking', label: 'Goodworking CRM' },
] as const satisfies readonly Option<System>[];

export const STATUS_OPTIONS = [
  { value: 'received', label: 'Recibido' },
  { value: 'queued', label: 'En cola' },
  { value: 'processing', label: 'Procesando' },
  { value: 'processed', label: 'Procesado' },
  { value: 'failed', label: 'Fallido' },
  { value: 'quarantined', label: 'En cuarentena' },
  { value: 'dead', label: 'Dead letter' },
] as const satisfies readonly Option<MessageStatus>[];

export const DIRECTION_OPTIONS = [
  { value: 'inbound', label: 'Entrante' },
  { value: 'outbound', label: 'Saliente' },
] as const satisfies readonly Option<MessageDirection>[];

/** Flujos del plan (docs/PLAN.md). El texto es el resumen operativo del flujo. */
export const FLOW_OPTIONS = [
  { value: 'F1', label: 'F1 · DecoraPOS ⇄ Janis (pedidos de tienda)' },
  { value: 'F2', label: 'F2 · Janis/POS → HubSpot (clientes y pedidos)' },
  { value: 'F3', label: 'F3 · SAP → Janis (stock)' },
  { value: 'F4', label: 'F4 · SAP → Janis (importaciones)' },
  { value: 'F5', label: 'F5 · Multivende → Janis (ventas)' },
  { value: 'F6', label: 'F6 · Facturador → Janis → Multivende (DTE)' },
  { value: 'F7', label: 'F7 · Janis → Multivende (stock)' },
  { value: 'F8', label: 'F8 · Syncro → Janis (precios)' },
  { value: 'F9', label: 'F9 · Altanet WMS ⇄ Janis (picking y despacho)' },
  { value: 'F10', label: 'F10 · Middleware → SAP (documentos)' },
  { value: 'F11', label: 'F11 · Devoluciones' },
  { value: 'F12', label: 'F12 · Syncro → Janis (catálogo)' },
  { value: 'F13', label: 'F13 · Syncro → DecoraPOS (catálogo)' },
  { value: 'F14', label: 'F14 · SAP → DecoraPOS (disponibilidad)' },
  { value: 'F15', label: 'F15 · SAP → Middleware (bodegas)' },
  { value: 'F16', label: 'F16 · Middleware → Janis (alta de bodegas)' },
  { value: 'F17', label: 'F17 · Janis → Middleware (reconciliación de stock)' },
  { value: 'F18', label: 'F18 · Janis → DecoraPOS (disponibilidad vendible)' },
  { value: 'F19', label: 'F19 · DecoraPOS → Middleware (recepciones en tienda)' },
  { value: 'F20', label: 'F20 · Syncro → HubSpot (catálogo CRM)' },
  { value: 'F21', label: 'F21 · Goodworking → SAP (reservas)' },
  { value: 'F23', label: 'F23 · Syncro → Goodworking (catálogo)' },
  { value: 'F24', label: 'F24 · SAP → Goodworking (disponibilidad)' },
  { value: 'F25', label: 'F25 · Middleware → SAP (entrega después de Janis)' },
] as const satisfies readonly Option<FlowId>[];

/**
 * Estado de una corrida entera. Los mismos textos que la caja de un paso en el
 * grafo: es el mismo estado, agregado con las mismas reglas.
 */
export const RUN_STATUS_OPTIONS = [
  { value: 'running', label: 'En curso' },
  { value: 'ok', label: 'Completada' },
  { value: 'partial', label: 'Con errores parciales' },
  { value: 'failed', label: 'Fallida' },
  { value: 'quarantined', label: 'En cuarentena' },
] as const satisfies readonly Option<FlowRunStatus>[];

/** Motivos de cuarentena, en el orden en que conviene leerlos. */
export const QUARANTINE_REASON_LABELS: Readonly<Record<string, string>> = {
  schema_validation: 'Validación de schema',
  unknown_event: 'Evento desconocido',
  unmapped_entity: 'Entidad sin mapeo',
};

/** Resoluciones posibles de una dead letter. */
export const RESOLUTION_LABELS: Readonly<Record<string, string>> = {
  replayed: 'Reprocesada',
  discarded: 'Descartada',
  edited: 'Editada y reprocesada',
};

function toLookup<T extends string>(
  options: readonly Option<T>[],
): Readonly<Record<string, string>> {
  const lookup: Record<string, string> = {};
  for (const option of options) {
    lookup[option.value] = option.label;
  }
  return lookup;
}

const SYSTEM_LABELS = toLookup(SYSTEM_OPTIONS);
const STATUS_LABELS = toLookup(STATUS_OPTIONS);
const DIRECTION_LABELS = toLookup(DIRECTION_OPTIONS);
const FLOW_LABELS = toLookup(FLOW_OPTIONS);
const RUN_STATUS_LABELS = toLookup(RUN_STATUS_OPTIONS);

export function runStatusLabel(value: string): string {
  return RUN_STATUS_LABELS[value] ?? value;
}

/** Etiqueta legible de un sistema; si llega uno desconocido se muestra tal cual. */
export function systemLabel(value: string): string {
  return SYSTEM_LABELS[value] ?? value;
}

export function statusLabel(value: string): string {
  return STATUS_LABELS[value] ?? value;
}

export function directionLabel(value: string): string {
  return DIRECTION_LABELS[value] ?? value;
}

/** Etiqueta larga del flujo (con descripcion). */
export function flowLabel(value: string | null): string {
  if (!value) {
    return 'Sin flujo';
  }
  return FLOW_LABELS[value] ?? value;
}

export function quarantineReasonLabel(value: string): string {
  return QUARANTINE_REASON_LABELS[value] ?? value;
}

export function resolutionLabel(value: string | null): string {
  if (!value) {
    return 'Sin resolver';
  }
  return RESOLUTION_LABELS[value] ?? value;
}
